import type { DirectoryOrganization } from "@repo/api/client";
import { TenantSlug } from "@repo/config/sharedConfig";
import { Button, Card, Flex, Icon, Text, TextField } from "@repo/ui";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useApiClient } from "../../contexts/ApiContext";
import { getTenantSlug } from "../../tenant";
import {
	type DirectorySpecialty,
	formatLocation,
	formatPhone,
	userFacingMessage,
} from "./directoryAdminUtils";
import { OrganizationForm } from "./OrganizationForm";
import { OrganizationProviders } from "./OrganizationProviders";

type Selection =
	| { kind: "none" }
	| { kind: "new" }
	| { kind: "organization"; organization: DirectoryOrganization };

/**
 * Add organizations and providers to the provider directory, and edit
 * existing organizations. For tenant admins only; the API enforces that.
 */
export function DirectoryAdminSection() {
	const apiClient = useApiClient();
	const [query, setQuery] = useState("");
	const [results, setResults] = useState<DirectoryOrganization[]>();
	const [isSearching, setIsSearching] = useState(false);
	const [selection, setSelection] = useState<Selection>({ kind: "none" });
	const [specialties, setSpecialties] = useState<DirectorySpecialty[]>([]);

	useEffect(() => {
		apiClient.directoryAdmin
			.listSpecialties()
			.then((result) => setSpecialties(result.specialties))
			.catch((error: unknown) => {
				console.error("Failed to load specialties:", error);
				toast.error(
					"We couldn't load the list of specialties. Reload the page to try again",
				);
			});
	}, [apiClient]);

	const handleSearch = async () => {
		setIsSearching(true);
		try {
			const result = await apiClient.directoryAdmin.searchOrganizations({
				query,
			});
			setResults(result.organizations);
			setSelection({ kind: "none" });
		} catch (error) {
			console.error("Failed to search organizations:", error);
			toast.error(
				userFacingMessage(
					error,
					"We couldn't search the directory. Please try again",
				),
			);
		} finally {
			setIsSearching(false);
		}
	};

	const showOrganization = (organization: DirectoryOrganization) => {
		setSelection({ kind: "organization", organization });
		setResults((current) =>
			current?.map((other) =>
				other.id === organization.id ? organization : other,
			),
		);
	};

	const selected =
		selection.kind === "organization" ? selection.organization : undefined;

	return (
		<Flex column gap="md">
			<Text size="lg" weight="semibold" color="sand12">
				Provider Directory
			</Text>
			<Text size="sm" color="sand11">
				{getTenantSlug() === TenantSlug.Demo
					? "Providers added on Demo are only visible to Demo. Organizations have no owner, so a new one is visible to other practices until it has a Demo provider. Add one right away."
					: "Changes here update the shared provider directory, which other practices use too. Search first, by name, NPI, phone or fax, so you don't add an organization twice."}
			</Text>

			<Flex gap="sm" align="center">
				<Flex column flex>
					<TextField
						aria-label="Search organizations"
						placeholder="Name, NPI, phone or fax"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter" && query.trim().length >= 2) {
								void handleSearch();
							}
						}}
						subtlePlaceholder={false}
					/>
				</Flex>
				<Button
					variant="outline"
					size="sm"
					isDisabled={isSearching || query.trim().length < 2}
					onPress={handleSearch}
				>
					<Flex gap="xs" align="center">
						<Icon name="Search" size="sm" />
						<Text size="sm">{isSearching ? "Searching..." : "Search"}</Text>
					</Flex>
				</Button>
				<Button size="sm" onPress={() => setSelection({ kind: "new" })}>
					<Flex gap="xs" align="center">
						<Icon name="Plus" size="sm" />
						<Text size="sm">New organization</Text>
					</Flex>
				</Button>
			</Flex>

			{selection.kind === "none" && results ? (
				results.length === 0 ? (
					<Text size="sm" color="sand11">
						No organizations match. Try another spelling, the phone or fax
						number, or add it as a new organization.
					</Text>
				) : (
					<Flex column gap="sm">
						{results.map((organization) => {
							const firstLocation = organization.locations[0];
							return (
								<Card
									key={organization.id}
									icon="Building"
									title={organization.companyName ?? "Unnamed organization"}
									description={
										firstLocation
											? formatLocation(firstLocation)
											: "No locations"
									}
									properties={[
										firstLocation?.fax
											? {
													icon: "FileText",
													text: `Fax ${formatPhone(firstLocation.fax)}`,
												}
											: null,
										{
											icon: "Stethoscope",
											text: `${organization.providers.length} provider${organization.providers.length === 1 ? "" : "s"}`,
										},
										organization.locations.length > 1
											? {
													icon: "MapPin",
													text: `${organization.locations.length} locations`,
												}
											: null,
									]}
									rightText={organization.source ?? undefined}
									onClick={() => showOrganization(organization)}
								/>
							);
						})}
					</Flex>
				)
			) : null}

			{selection.kind !== "none" ? (
				<Flex
					column
					gap="xl"
					padding="lg"
					background="sand1"
					borderWidth="md"
					borderColor="sandA5"
					borderRadius="md"
				>
					<Flex justify="space-between" align="center">
						<Text size="md" weight="semibold" color="sand12">
							{selected
								? (selected.companyName ?? "Unnamed organization")
								: "New organization"}
						</Text>
						<Button
							variant="ghost"
							size="xs"
							onPress={() => setSelection({ kind: "none" })}
						>
							<Flex gap="xxs" align="center">
								<Icon name="ChevronLeft" size="xs" />
								<Text size="sm">{results ? "Back to results" : "Close"}</Text>
							</Flex>
						</Button>
					</Flex>
					<OrganizationForm
						// Remount once a save adds locations, so the draft picks up their
						// ids. Otherwise saving again would create them a second time.
						key={
							selected ? `${selected.id}-${selected.locations.length}` : "new"
						}
						organization={selected}
						onSaved={showOrganization}
					/>
					{selected ? (
						<OrganizationProviders
							// Remount when locations change so a new one shows up unticked
							// and an org's only location is pre-ticked.
							key={`${selected.id}-${selected.locations.length}`}
							organization={selected}
							specialties={specialties}
							onChanged={showOrganization}
						/>
					) : null}
				</Flex>
			) : null}
		</Flex>
	);
}
